import React from 'react'
import { Loader, Users } from 'lucide-react'
import { cn } from '@/utils'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { OrgRole } from '@/types/organization'
import {
  MemberAvatar,
  ORG_DANGER_BUTTON,
  ORG_GHOST_BUTTON,
  ORG_LABEL,
  ORG_SELECT_CONTENT,
  ORG_SELECT_ITEM,
  ORG_SELECT_TRIGGER,
  ORG_SURFACE,
  OrgEmptyState,
  RoleBadge,
  SelfBadge,
  memberName,
} from '../OrgPrimitives'
import {
  OrgMembersListBaseProps,
  ROLE_LABELS,
  ROLE_OPTIONS,
  canChangeRoleOfRow,
  canRemoveRow,
  canViewActivity,
} from './OrgMembersListBase'

/**
 * Mobile members list — cards rather than a table, following `InquiriesTable`.
 *
 * Same permission rules as the desktop view: the activity tiles and the action
 * buttons only render for OWNER/ADMIN.
 */
export const OrgMembersListMobile: React.FC<OrgMembersListBaseProps> = (props) => {
  const { rows, isLoading, canManage, selectedUserId, pendingUserId } = props
  const showActivity = canViewActivity(props)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-8">
        <Loader className="animate-spin text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]" size={20} />
        <span className="font-plus-jakarta text-[14px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
          Loading members...
        </span>
      </div>
    )
  }

  if (rows.length === 0) {
    return (
      <OrgEmptyState
        icon={Users}
        title="No members yet"
        description="Invite a colleague to get started."
        className="py-6"
      />
    )
  }

  return (
    <div className="space-y-3">
      {rows.map((row) => {
        const isSelected = selectedUserId === row.membership.user_id
        const isPending = pendingUserId === row.membership.user_id
        const showRoleSelect = canChangeRoleOfRow(row, props)
        const showRemove = canRemoveRow(row, props)

        return (
          <div
            key={row.membership.id}
            className={cn(
              ORG_SURFACE,
              'p-3',
              isSelected && 'ring-1 ring-inset ring-[rgba(45,41,35,0.32)] dark:ring-[rgba(253,253,253,0.32)]',
            )}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <MemberAvatar member={row.membership} size={36} />
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
                      {memberName(row.membership)}
                    </span>
                    {row.isSelf && <SelfBadge />}
                  </div>
                  <p className="truncate font-plus-jakarta text-[12px] font-normal leading-[16px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
                    {row.membership.user?.email ?? '—'}
                  </p>
                </div>
              </div>
              {!showRoleSelect && <RoleBadge role={row.membership.role} className="shrink-0" />}
            </div>

            {showRoleSelect && (
              <div className="mt-3">
                <p className={ORG_LABEL}>Role</p>
                <Select
                  value={row.membership.role}
                  disabled={isPending}
                  onValueChange={(value) => props.onChangeRole(row, value as OrgRole)}
                >
                  <SelectTrigger className={cn(ORG_SELECT_TRIGGER, 'mt-1 h-12 w-full')}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={ORG_SELECT_CONTENT}>
                    {ROLE_OPTIONS.map((role) => (
                      <SelectItem key={role} value={role} className={ORG_SELECT_ITEM}>
                        {ROLE_LABELS[role]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {showActivity && (
              <div className="mt-3 grid grid-cols-3 gap-2">
                <div>
                  <p className={ORG_LABEL}>Bids</p>
                  <p className="font-plus-jakarta text-[14px] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
                    {row.counts?.bids ?? 0}
                  </p>
                </div>
                <div>
                  <p className={ORG_LABEL}>RFQs</p>
                  <p className="font-plus-jakarta text-[14px] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
                    {row.counts?.rfqs ?? 0}
                  </p>
                </div>
                <div>
                  <p className={ORG_LABEL}>Total</p>
                  <p className="font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
                    {row.counts?.total ?? 0}
                  </p>
                </div>
              </div>
            )}

            {canManage && (
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={() => props.onViewActivity(row)}
                  className={cn(ORG_GHOST_BUTTON, 'flex-1')}
                >
                  {isSelected ? 'Hide activity' : 'View activity'}
                </button>
                {showRemove && (
                  <button
                    type="button"
                    onClick={() => props.onRemove(row)}
                    disabled={isPending}
                    className={cn(ORG_DANGER_BUTTON, 'flex-1')}
                  >
                    Remove
                  </button>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default OrgMembersListMobile
